import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { setInstallmentsList, setInstallmentsToPay, setTotalToPay } from 'v3/data/store/installments/actions';
import { InstallmentStatusEnum } from 'v3/data/store/installments/enum';
import { IContractingDetailsParams } from 'v3/pages/Contracting/Details/Cards/types';
import * as api from 'v3/services/api';
import { IInstallment } from 'v3/services/interfaces/response';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';

const isContiguous = (numbers: number[]): boolean => {
  if (numbers.length <= 1) return true;
  const sortedNumbers = [...numbers].sort((a, b) => a - b);
  for (let i = 0; i < sortedNumbers.length - 1; i++) {
    if (sortedNumbers[i + 1] !== sortedNumbers[i] + 1) {
      return false;
    }
  }
  return true;
};

export const useInstallmentsLogic = () => {
  const [selectedInstallments, setSelectedInstallments] = useState<number[]>([]);
  const { item, itemsToPay } = useStoreSelector((store) => store.installments);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPixDisplayModeModalOpen, setIsPixDisplayModeModalOpen] = useState(false);
  const [isPixSMSConfirmationModalOpen, setIixSMSConfirmationModalOpen] = useState(false);
  const [isPixQRCodeModalOpen, setIsPixQRCodeModalOpen] = useState(false);
  const [isSuccessfulPaymentModalOpen, setIsSuccessfulPaymentModalOpen] = useState(false);
  const [isUnsuccessfulPaymentModalOpen, setIsUnsuccessfulPaymentModalOpen] = useState(false);
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;

  const calculateAndSetItemsToPay = useCallback((currentSelectedNumbers: number[], allItems: IInstallment[]) => {
    const selectedInstallmentObjects = allItems.filter((item) => currentSelectedNumbers.includes(item.number));
    const newTotalToPay = selectedInstallmentObjects.reduce((sum, item) => sum + item.presentAmount, 0);

    setInstallmentsToPay(selectedInstallmentObjects);
    setTotalToPay(newTotalToPay);
  }, []);

  const unpaidItems: IInstallment[] = item.installments.filter(
    (item) => item.paymentStatus !== InstallmentStatusEnum.PAID,
  );

  const handleCheckboxChange = useCallback(
    (installmentNumber: number) => {
      setSelectedInstallments((prevSelected) => {
        const isCurrentlySelected = prevSelected.includes(installmentNumber);

        const overdueNumbers = item.installments
          .filter((item) => item.paymentStatus === InstallmentStatusEnum.LATE)
          .map((item) => item.number);

        if (overdueNumbers.includes(installmentNumber)) {
          return prevSelected;
        }

        let candidateSelectedNumbers: number[];

        if (isCurrentlySelected) {
          const userControlledSelected = prevSelected.filter((n) => !overdueNumbers.includes(n));
          const minUserControlled = userControlledSelected.length > 0 ? Math.min(...userControlledSelected) : undefined;
          const maxUserControlled = userControlledSelected.length > 0 ? Math.max(...userControlledSelected) : undefined;

          if (installmentNumber === minUserControlled || installmentNumber === maxUserControlled) {
            candidateSelectedNumbers = prevSelected.filter((num) => num !== installmentNumber);
          } else {
            return prevSelected;
          }
        } else {
          if (prevSelected.length === 0 || prevSelected.every((num) => overdueNumbers.includes(num))) {
            const allPendingNumbers = unpaidItems
              .filter((item) => item.paymentStatus === InstallmentStatusEnum.PENDING_PAYMENT)
              .map((item) => item.number)
              .sort((a, b) => a - b);

            if (overdueNumbers.length > 0) {
              const maxOverdue = Math.max(...overdueNumbers);
              if (installmentNumber === maxOverdue + 1) {
                candidateSelectedNumbers = [...prevSelected, installmentNumber];
              } else {
                return prevSelected;
              }
            } else {
              const minPending = allPendingNumbers.length > 0 ? allPendingNumbers[0] : undefined;
              const maxPending =
                allPendingNumbers.length > 0 ? allPendingNumbers[allPendingNumbers.length - 1] : undefined;

              if (installmentNumber === minPending || installmentNumber === maxPending) {
                candidateSelectedNumbers = [...prevSelected, installmentNumber];
              } else {
                return prevSelected;
              }
            }
          } else {
            const minOverallSelected = Math.min(...prevSelected);
            const maxOverallSelected = Math.max(...prevSelected);

            if (installmentNumber === minOverallSelected - 1 || installmentNumber === maxOverallSelected + 1) {
              candidateSelectedNumbers = [...prevSelected, installmentNumber];
            } else {
              return prevSelected;
            }
          }
        }

        const finalNewSelection = [...new Set([...overdueNumbers, ...candidateSelectedNumbers])].sort((a, b) => a - b);

        if (!isContiguous(finalNewSelection)) {
          return prevSelected;
        }

        calculateAndSetItemsToPay(finalNewSelection, item.installments);

        return finalNewSelection;
      });
    },
    [calculateAndSetItemsToPay, item.installments, unpaidItems],
  );

  const canInstallmentBeToggled = useCallback(
    (installment: IInstallment) => {
      const installmentNumber = installment.number;
      const isCurrentlySelected = selectedInstallments.includes(installmentNumber);

      const overdueNumbers = item.installments
        .filter((i) => i.paymentStatus === InstallmentStatusEnum.LATE)
        .map((i) => i.number);

      if (overdueNumbers.includes(installmentNumber)) {
        return false;
      }

      const allUnpaidSelected = selectedInstallments.length === unpaidItems.length && unpaidItems.length > 0;

      if (allUnpaidSelected) {
        const hasLateInstallments = overdueNumbers.length > 0;

        if (hasLateInstallments) {
          const maxSelected = Math.max(...selectedInstallments);
          return installmentNumber === maxSelected;
        } else {
          const minSelected = Math.min(...selectedInstallments);
          const maxSelected = Math.max(...selectedInstallments);
          return installmentNumber === minSelected || installmentNumber === maxSelected;
        }
      } else {
        let hypotheticalCandidateSelectedNumbers: number[] = [];
        let canDeselectCurrent = false;

        if (isCurrentlySelected) {
          const userControlledSelected = selectedInstallments.filter((n) => !overdueNumbers.includes(n));
          const minUserControlled = userControlledSelected.length > 0 ? Math.min(...userControlledSelected) : undefined;
          const maxUserControlled = userControlledSelected.length > 0 ? Math.max(...userControlledSelected) : undefined;

          if (userControlledSelected.length === 1 && installmentNumber === minUserControlled) {
            canDeselectCurrent = true;
          } else if (userControlledSelected.length > 1) {
            const isMinSideAvailableForExpansion =
              minUserControlled !== undefined &&
              unpaidItems.some((i) => i.number === minUserControlled - 1) &&
              !selectedInstallments.includes(minUserControlled - 1);
            const isMaxSideAvailableForExpansion =
              maxUserControlled !== undefined &&
              unpaidItems.some((i) => i.number === maxUserControlled + 1) &&
              !selectedInstallments.includes(maxUserControlled + 1);

            if (installmentNumber === minUserControlled && isMinSideAvailableForExpansion) {
              canDeselectCurrent = true;
            } else if (installmentNumber === maxUserControlled && isMaxSideAvailableForExpansion) {
              canDeselectCurrent = true;
            }
          }

          if (canDeselectCurrent) {
            hypotheticalCandidateSelectedNumbers = selectedInstallments.filter((num) => num !== installmentNumber);
          } else {
            return false;
          }
        } else {
          if (selectedInstallments.length === 0 || selectedInstallments.every((num) => overdueNumbers.includes(num))) {
            const allPendingNumbers = unpaidItems
              .filter((i) => i.paymentStatus === InstallmentStatusEnum.PENDING_PAYMENT)
              .map((i) => i.number)
              .sort((a, b) => a - b);

            if (overdueNumbers.length > 0) {
              const maxOverdue = Math.max(...overdueNumbers);
              if (installmentNumber === maxOverdue + 1) {
                hypotheticalCandidateSelectedNumbers = [...selectedInstallments, installmentNumber];
              } else {
                return false;
              }
            } else {
              const minPending = allPendingNumbers.length > 0 ? allPendingNumbers[0] : undefined;
              const maxPending =
                allPendingNumbers.length > 0 ? allPendingNumbers[allPendingNumbers.length - 1] : undefined;

              if (installmentNumber === minPending || installmentNumber === maxPending) {
                hypotheticalCandidateSelectedNumbers = [...selectedInstallments, installmentNumber];
              } else {
                return false;
              }
            }
          } else {
            const minOverallSelected = Math.min(...selectedInstallments);
            const maxOverallSelected = Math.max(...selectedInstallments);

            if (installmentNumber === minOverallSelected - 1 || installmentNumber === maxOverallSelected + 1) {
              hypotheticalCandidateSelectedNumbers = [...selectedInstallments, installmentNumber];
            } else {
              return false;
            }
          }
        }

        const hypotheticalFinalNewSelection = [
          ...new Set([...overdueNumbers, ...hypotheticalCandidateSelectedNumbers]),
        ].sort((a, b) => a - b);

        return isContiguous(hypotheticalFinalNewSelection);
      }
    },
    [selectedInstallments, item.installments, unpaidItems],
  );

  const handleInstallments = useCallback(async () => {
    try {
      const featureFlagResponse = await api.getFlagStatus(FeatureFlagEnum.IKCLOUD_33367_ENABLE_PIX_PAYMENT);
      if (featureFlagResponse.data.enabled) {
        const response = await api.getInstallments(contractingKey);
        setInstallmentsList(response.data);

        const overdueItemsNumbers = response.data.installments
          .filter((item) => item.paymentStatus === InstallmentStatusEnum.LATE)
          .map((item) => item.number);

        setSelectedInstallments(overdueItemsNumbers);
        calculateAndSetItemsToPay(overdueItemsNumbers, response.data.installments);
      }
    } catch (error) {
      showErrorDialog('Erro ao carregar as parcelas', error);
    }
  }, [calculateAndSetItemsToPay, setInstallmentsList, showErrorDialog, contractingKey]);

  useEffect(() => {
    return () => {
      setInstallmentsList({
        contractId: '',
        installments: [],
      });
      setInstallmentsToPay([]);
      setTotalToPay(0);
    };
  }, [setInstallmentsList]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenPixDisplayModeModal = useCallback(() => {
    setIsPixDisplayModeModalOpen(true);
  }, []);

  const handleClosePixDisplayModeModal = useCallback(() => {
    setIsPixDisplayModeModalOpen(false);
  }, []);

  const handleConfirmInstallmentsAndOpenPix = useCallback(() => {
    setIsModalOpen(false);
    setIsPixDisplayModeModalOpen(true);
  }, []);

  const handleConfirmPixSendingSMS = useCallback(() => {
    setIsPixDisplayModeModalOpen(false);
    setIixSMSConfirmationModalOpen(true);
  }, []);

  const handleClosePixSendingSMS = useCallback(() => {
    setIixSMSConfirmationModalOpen(false);
  }, []);

  const handleConfirmPixQRCode = useCallback(() => {
    setIsPixDisplayModeModalOpen(false);
    setIsPixQRCodeModalOpen(true);
  }, []);

  const handleClosePixQRCode = useCallback(() => {
    setIsPixQRCodeModalOpen(false);
  }, []);

  const handleCloseSuccessfulPaymentModal = useCallback(async () => {
    setIsSuccessfulPaymentModalOpen(false);
    setInstallmentsToPay([]);
    await handleInstallments();
  }, []);

  const handleOpenSuccessfulPaymentModal = useCallback(async () => {
    setIsPixQRCodeModalOpen(false);
    setIsSuccessfulPaymentModalOpen(true);
  }, []);

  const handleCloseUnsuccessfulPaymentModal = useCallback(async () => {
    setIsUnsuccessfulPaymentModalOpen(false);
    setInstallmentsToPay([]);

    await handleInstallments();
  }, []);

  const handleOpenUnsuccessfulPaymentModal = useCallback(async () => {
    setIsPixQRCodeModalOpen(false);
    setIsUnsuccessfulPaymentModalOpen(true);
  }, []);

  return {
    selectedInstallments,
    unpaidItems,
    item,
    itemsToPay,
    isModalOpen,
    handleCheckboxChange,
    canInstallmentBeToggled,
    handleOpenModal,
    handleCloseModal,
    isPixDisplayModeModalOpen,
    isPixSMSConfirmationModalOpen,
    handleOpenPixDisplayModeModal,
    handleClosePixDisplayModeModal,
    handleConfirmInstallmentsAndOpenPix,
    handleConfirmPixSendingSMS,
    handleClosePixSendingSMS,
    handleConfirmPixQRCode,
    isPixQRCodeModalOpen,
    handleClosePixQRCode,
    isSuccessfulPaymentModalOpen,
    handleCloseSuccessfulPaymentModal,
    isUnsuccessfulPaymentModalOpen,
    handleCloseUnsuccessfulPaymentModal,
    handleOpenSuccessfulPaymentModal,
  };
};